import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit'
import { RootState } from '@/store/store'
import { setFileProcessingStatus, FileProcessingStatus } from '@/store/slices/file-upload-tasks.slice'
import { AgentEventsActions } from '@/agent/events/store/slice'
import { AgentEventType, ClientAgentEventRead } from '@/agent/events/store/schemas'
import { TemplateUploadErrorType, AnalyticsEvent } from '@/analytics/schema/events.schema'
import { getTrackEventGlobal } from '@/analytics/utils/analytics-helpers'
import { selectAgentConversationFormTemplateFilePaths } from '@/agent/chat_form/store/selectors'

const templateAnalytics = createListenerMiddleware()
const start = templateAnalytics.startListening

// Helper map to track upload start times
const uploadStartTimes = new Map<string, number>()

// Track upload start when a template file is added (PENDING status)
start({
  actionCreator: setFileProcessingStatus,
  effect: (action, api) => {
    const { sourcePath, status } = action.payload
    const state = api.getState() as RootState
    const conversationId = Object.keys(state.agentConversationFormState.forms).find((cid) => {
      return state.agentConversationFormState.forms[cid]?.requestParams.template_paths.includes(sourcePath)
    })
    if (!conversationId) return

    const track = getTrackEventGlobal()
    if (!track) return

    if (status === FileProcessingStatus.PENDING) {
      uploadStartTimes.set(sourcePath, Date.now())
      track(AnalyticsEvent.TemplateUploadStarted, { conversationId, filePath: sourcePath })
    } else if (status === FileProcessingStatus.SUCCESS || status === FileProcessingStatus.ERROR) {
      const startTs = uploadStartTimes.get(sourcePath)
      if (startTs) {
        const durationMs = Date.now() - startTs
        const ok = status === FileProcessingStatus.SUCCESS
        track(ok ? AnalyticsEvent.TemplateUploadCompleted : AnalyticsEvent.TemplateUploadFailed, {
          conversationId,
          filePath: sourcePath,
          durationMs,
          ...(ok ? {} : { errorType: TemplateUploadErrorType.processing }),
        })
        uploadStartTimes.delete(sourcePath)
      }
    }
  },
})

// Track response events
start({
  matcher: isAnyOf(AgentEventsActions.upsertEvents),
  effect: (action, api) => {
    const { conversationId, events } = action.payload as {
      conversationId: string
      events: ClientAgentEventRead[]
    }
    const evt = events[0]
    const track = getTrackEventGlobal()
    if (!track) return

    const templatePaths = selectAgentConversationFormTemplateFilePaths(api.getState() as RootState, { conversationId })
    if (templatePaths.length === 0) return

    switch (evt.type) {
      case AgentEventType.AGENT_RESPONSE:
        track(AnalyticsEvent.TemplateResponseSucceeded, { conversationId })
        break
      case AgentEventType.SERVER_ERROR:
      case AgentEventType.CLIENT_ERROR_EVENT:
        track(AnalyticsEvent.TemplateResponseFailed, {
          conversationId,
          errorType: evt.type,
        })
        break
      case AgentEventType.STOP_REQUEST_RECEIVED:
        track(AnalyticsEvent.TemplateResponseCanceled, { conversationId })
        break
    }
  },
})

export default templateAnalytics
